import React, { useState, useEffect, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import type { InventoryItem, RoomPhoto } from '../types';
import { calculateImageDimensions } from '../utils/imageUtils'; // Import the correct utility
import PhotoCapture from './PhotoCapture';
import { PhotoData } from '../services/CameraService';
import { deletePhotoFromSupabase } from '../utils/supabaseStorage';
import PhotoGalleryModal from './PhotoGalleryModal';

export interface RoomInventoryItemProps {
  item: InventoryItem;
  roomIndex: number;
  itemIndex: number;
  updateRoomItem: (roomIndex: number, itemIndex: number, updates: Partial<InventoryItem>) => void;
  removeItemFromRoom: (roomIndex: number, itemIndex: number) => void;
  renderCameraButtonProp: () => JSX.Element;
  renderGalleryButtonProp: () => JSX.Element;
  addItemAfter: (roomIndex: number, itemIndex: number) => void;
  isProcessing?: boolean;
}

const RoomInventoryItem: React.FC<RoomInventoryItemProps> = ({
  item,
  roomIndex,
  itemIndex,
  updateRoomItem,
  removeItemFromRoom,
  renderCameraButtonProp,
  renderGalleryButtonProp,
  addItemAfter,
  isProcessing
}) => {
  const [category, setCategory] = useState(item.category || '');
  const [quantity, setQuantity] = useState(item.quantity || '1');
  const [condition, setCondition] = useState(item.condition || '');
  const [notes, setNotes] = useState(item.notes || '');
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [isAddingPhotos, setIsAddingPhotos] = useState(false);

  const isUpdatingRef = useRef(false);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isUpdatingRef.current) {
      setCategory(item.category || '');
      setQuantity(item.quantity || '1');
      setCondition(item.condition || '');
      setNotes(item.notes || '');
    }
  }, [item]);

  // Scroll detection to prevent field selection during scroll
  useEffect(() => {
    const handleScroll = () => {
      isScrollingRef.current = true;
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('touchmove', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchmove', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const updateWithScrollPreservation = (field: keyof InventoryItem, value: string) => {
    isUpdatingRef.current = true;
    updateRoomItem(roomIndex, itemIndex, { [field]: value });
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
  };

  const handleBlur = (field: keyof InventoryItem, value: string) => {
    updateWithScrollPreservation(field, value);
  };

  const incrementQuantity = () => {
    const newValue = String(parseInt(quantity || '0') + 1);
    setQuantity(newValue);
    updateWithScrollPreservation('quantity', newValue);
  };

  const decrementQuantity = () => {
    const currentValue = parseInt(quantity || '0');
    if (currentValue > 1) {
      const newValue = String(currentValue - 1);
      setQuantity(newValue);
      updateWithScrollPreservation('quantity', newValue);
    }
  };

  const removePhoto = async (photoIndex: number) => {
    try {
      console.log(`[RoomInventoryItem] Removing photo at index ${photoIndex}`);
      
      // Set update flag to prevent UI flickering
      isUpdatingRef.current = true;
      
      // Make a copy of the photos array
      const currentPhotos = item.photos || [];
      
      // Validate index is within bounds
      if (photoIndex < 0 || photoIndex >= currentPhotos.length) {
        console.error(`[RoomInventoryItem] Invalid photo index: ${photoIndex}, photos length: ${currentPhotos.length}`);
        isUpdatingRef.current = false;
        return;
      }
      
      // Get the photo to be removed
      const photoToRemove = currentPhotos[photoIndex];
      console.log(`[RoomInventoryItem] Removing photo with ID: ${photoToRemove?.id || 'unknown'}`);
      
      // Delete from Supabase Storage if it has a fileName
      if (photoToRemove && photoToRemove.fileName) {
        try {
          console.log(`[RoomInventoryItem] Deleting from storage: ${photoToRemove.fileName}`);
          await deletePhotoFromSupabase(photoToRemove.fileName, 'inventory-photos');
          console.log(`[RoomInventoryItem] ✅ Deleted from storage`);
        } catch (storageError) {
          console.warn(`[RoomInventoryItem] ⚠️ Failed to delete from storage:`, storageError);
          // Continue with state deletion even if storage deletion fails
        }
      }
      
      // Create a new array without the specified photo
      const newPhotos = currentPhotos.filter((_, idx) => idx !== photoIndex);
      
      console.log(`[RoomInventoryItem] Photo removed, new photos array length: ${newPhotos.length}`);
      
      // Force update with the new photos array - directly call parent update function
      updateRoomItem(roomIndex, itemIndex, { photos: newPhotos });
      
      // Reset the update flag after a short delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 300);
    } catch (error) {
      console.error('[RoomInventoryItem] Error removing photo:', error);
      isUpdatingRef.current = false;
    }
  };

  const openGallery = () => {
    setShowGalleryModal(true);
  };

  const closeGallery = () => {
    setShowGalleryModal(false);
  };
  
  const handlePhotosCaptured = async (photos: PhotoData[]) => {
    setIsAddingPhotos(false);
    
    // Create photo metadata WITHOUT url (will be uploaded)
    const newPhotos: RoomPhoto[] = photos.map(photo => ({
      id: `photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      fileName: `photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.jpg`,
      localPath: `data:image/${photo.format};base64,${photo.base64String}`,
      url: undefined, // ✅ Will be set after upload
      timestamp: new Date().toISOString(),
      uploading: true, // ✅ Mark as uploading
      processed: false,
      retries: 0,
      metadata: {
        originalSize: 0,
        compressedSize: 0,
      }
    }));
    
    // Add photos to state immediately (with uploading flag)
    const currentPhotos = item.photos || [];
    const updatedPhotos = [...currentPhotos, ...newPhotos];
    
    isUpdatingRef.current = true;
    updateRoomItem(roomIndex, itemIndex, { photos: updatedPhotos });
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
    
    // ✅ IMMEDIATE UPLOAD: Upload each photo to Supabase Storage
    const { uploadPhotoToSupabase } = await import('../utils/supabaseStorage');
    
    let successCount = 0;
    let failCount = 0;
    
    for (const photo of newPhotos) {
      try {
        console.log(`[RoomInventoryItem] 📤 Uploading photo: ${photo.fileName}`);
        
        // Upload to Supabase Storage
        const supabaseUrl = await uploadPhotoToSupabase(
          photo.localPath!,
          photo.fileName,
          'inventory-photos'
        );
        
        // Update photo with CDN URL
        const currentPhotosAfterUpload = item.photos || [];
        const updatedPhotosAfterUpload = currentPhotosAfterUpload.map(p => 
          p.id === photo.id 
            ? { ...p, url: supabaseUrl, uploading: false, error: undefined, localPath: undefined }
            : p
        );
        
        updateRoomItem(roomIndex, itemIndex, { photos: updatedPhotosAfterUpload });
        successCount++;
        
        console.log(`[RoomInventoryItem] ✅ Photo uploaded (${successCount}/${newPhotos.length}): ${photo.fileName}`);
      } catch (error) {
        console.error(`[RoomInventoryItem] ❌ Upload failed: ${photo.fileName}`, error);
        failCount++;
        
        // Mark photo with error (but keep localPath for recovery)
        const currentPhotosAfterError = item.photos || [];
        const updatedPhotosAfterError = currentPhotosAfterError.map(p => 
          p.id === photo.id 
            ? { ...p, uploading: false, error: `Upload failed: ${(error as Error).message}` }
            : p
        );
        
        updateRoomItem(roomIndex, itemIndex, { photos: updatedPhotosAfterError });
      }
    }
    
    // ✅ FORCE IMMEDIATE SAVE: Trigger window event to force autosave after uploads complete
    console.log(`[RoomInventoryItem] 💾 Upload batch complete: ${successCount} succeeded, ${failCount} failed - triggering immediate save`);
    
    // Dispatch custom event that PropertyInventory can listen to
    window.dispatchEvent(new CustomEvent('inventory-photos-uploaded', { 
      detail: { successCount, failCount, roomIndex, itemIndex } 
    }));
  };


  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderRadius: '16px',
        border: '1px solid rgba(148, 163, 184, 0.15)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 0 1px rgba(0, 0, 0, 0.06)',
        padding: '24px',
        marginBottom: '16px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0
      }}
      id={`room-item-${roomIndex}-${itemIndex}`}
    >
      <div style={{ display: 'grid', gap: '16px' }}>
        <div>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            fontSize: '13px', 
            fontWeight: '650', 
            color: '#0f172a',
            marginBottom: '8px'
          }}>
            <span style={{ width: '6px', height: '6px', background: '#f59e0b', borderRadius: '50%', marginRight: '8px' }}></span>
            Category
          </label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            onFocus={(e) => {
              if (isScrollingRef.current) {
                e.target.blur();
              }
            }}
            onBlur={() => handleBlur('category', category)}
            style={{
              width: '100%',
              padding: '12px',
              background: '#ffffff',
              border: '1px solid rgba(148, 163, 184, 0.15)',
              borderRadius: '14px',
              outline: 'none',
              fontSize: '14px',
              color: '#0f172a',
              transition: 'all 0.18s ease',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)'
            }}
            placeholder="e.g., Flooring/Carpet, Walls"
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '140px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontSize: '13px', 
              fontWeight: '650', 
              color: '#0f172a',
              marginBottom: '8px'
            }}>
              <span style={{ width: '6px', height: '6px', background: '#f59e0b', borderRadius: '50%', marginRight: '8px' }}></span>
              Quantity
            </label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                onClick={decrementQuantity}
                style={{
                  padding: '12px',
                  background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  borderRight: 'none',
                  borderRadius: '14px 0 0 14px',
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  color: '#475569'
                }}
                aria-label="Decrease quantity"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <input
                type="text"
                value={quantity}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const newValue = value === '' ? '1' : value;
                  setQuantity(newValue);
                }}
                onFocus={(e) => {
                  if (isScrollingRef.current) {
                    e.target.blur();
                  }
                }}
                onBlur={() => {
                  const validValue = quantity === '' ? '1' : quantity;
                  setQuantity(validValue);
                  handleBlur('quantity', validValue);
                }}
                style={{
                  width: '60px',
                  padding: '12px',
                  background: '#ffffff',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  borderLeft: 'none',
                  borderRight: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#0f172a',
                  textAlign: 'center'
                }}
                aria-label="Quantity"
              />
              <button
                type="button"
                onClick={incrementQuantity}
                style={{
                  padding: '12px',
                  background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  borderLeft: 'none',
                  borderRadius: '0 14px 14px 0',
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  color: '#475569'
                }}
                aria-label="Increase quantity"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>

          <div style={{ flex: '1', minWidth: '140px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontSize: '13px', 
              fontWeight: '650', 
              color: '#0f172a',
              marginBottom: '8px'
            }}>
              <span style={{ width: '6px', height: '6px', background: '#f59e0b', borderRadius: '50%', marginRight: '8px' }}></span>
              Condition
            </label>
            <select
              value={condition}
              onChange={(e) => {
                setCondition(e.target.value);
                handleBlur('condition', e.target.value);
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: '#ffffff',
                border: '1px solid rgba(148, 163, 184, 0.15)',
                borderRadius: '14px',
                outline: 'none',
                fontSize: '14px',
                color: '#0f172a',
                cursor: 'pointer',
                transition: 'all 0.18s ease',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)'
              }}
            >
              <option value="">Select Condition</option>
              <option value="Brand new">Brand new</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
              <option value="Poor">Poor</option>
              <option value="Very Poor">Very Poor</option>
            </select>
          </div>
        </div>

        <div>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            fontSize: '13px', 
            fontWeight: '650', 
            color: '#0f172a',
            marginBottom: '8px'
          }}>
            <span style={{ width: '6px', height: '6px', background: '#f59e0b', borderRadius: '50%', marginRight: '8px' }}></span>
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onFocus={(e) => {
              if (isScrollingRef.current) {
                e.target.blur();
              }
            }}
            onBlur={() => handleBlur('notes', notes)}
            rows={2}
            style={{
              width: '100%',
              padding: '12px',
              background: '#ffffff',
              border: '1px solid rgba(148, 163, 184, 0.15)',
              borderRadius: '14px',
              outline: 'none',
              fontSize: '14px',
              color: '#0f172a',
              transition: 'all 0.18s ease',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)',
              minHeight: '80px',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
            placeholder="Any additional notes or details about the item"
          />
        </div>

        <div style={{ marginTop: '4px' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            fontSize: '13px', 
            fontWeight: '650', 
            color: '#0f172a',
            marginBottom: '8px'
          }}>
            <span style={{ width: '6px', height: '6px', background: '#f59e0b', borderRadius: '50%', marginRight: '8px' }}></span>
            Photos {item.photos?.length ? `(${item.photos.length})` : ''}
          </label>

      {item.photos && item.photos.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                openGallery();
              }}
              className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-yellow-400 flex-shrink-0 mt-2"
            >
              <img
                src={item.photos[item.photos.length - 1]?.url || item.photos[item.photos.length - 1]?.localPath || ''}
                alt={item.category || 'Item'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute top-1 right-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                <span className="text-[10px]">📷</span>
                {item.photos.length}
              </div>
            </button>
      )}
      
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              {renderGalleryButtonProp()}
            </div>
            <div>
              {renderCameraButtonProp()}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-between mt-2 pt-3 border-t-2 border-gray-100">
          <div>
            <button
                onClick={() => addItemAfter(roomIndex, itemIndex)}
              className="group flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-yellow-50 text-gray-700 hover:text-yellow-600 border-2 border-gray-200 hover:border-yellow-300 rounded-lg text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-1 shadow-sm hover:shadow-md"
                aria-label="Add item after"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="text-yellow-500 group-hover:scale-110 transition-transform duration-200"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>Add Item</span>
            </button>
          </div>
            
          <div>
            <button
                onClick={() => removeItemFromRoom(roomIndex, itemIndex)}
              className="group flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-red-50 text-gray-700 hover:text-red-600 border-2 border-gray-200 hover:border-red-300 rounded-lg text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1 shadow-sm hover:shadow-md"
                aria-label="Remove item"
            >
              <Trash2 
                size={16} 
                className="text-red-500 group-hover:scale-110 transition-transform duration-200" 
              />
                <span>Remove</span>
            </button>
          </div>
        </div>
      </div>

      {/* PhotoGalleryModal - Same as Retrofit Survey */}
      {showGalleryModal && item.photos && item.photos.length > 0 && (
        <PhotoGalleryModal
          isOpen={showGalleryModal}
          onClose={closeGallery}
          photos={item.photos.map(photo => ({
            id: photo.id,
            url: photo.url,
            localPath: photo.localPath,
            fileName: photo.fileName,
          }))}
          sectionTitle={item.category || 'Item Photos'}
          onDeletePhoto={(photoId) => {
            const photoIndex = item.photos?.findIndex(p => p.id === photoId);
            if (photoIndex !== undefined && photoIndex !== -1) {
              removePhoto(photoIndex);
            }
          }}
          onUpdateNote={(photoId, note) => {
            // Update photo note if needed
            console.log('[RoomInventoryItem] Update note for photo:', photoId, note);
          }}
        />
      )}
    </div>
  );
};

export default RoomInventoryItem; 